import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SECTORS } from "@/lib/sectors";
import BrandMark from "@/components/BrandMark";
import {
  Zap, Bot, MessageSquare, TrendingUp, ShieldCheck, Check, ArrowRight,
  Clock, Sparkles, Lock, Phone, Users2, Wand2, Building2,
} from "lucide-react";

const VALUES = [
  {
    icon: MessageSquare,
    title: "Atiende a tus clientes 24/7",
    desc: "Un asistente que responde por web y WhatsApp, coge reservas y citas y resuelve dudas a cualquier hora. Nunca más una llamada o un mensaje sin contestar.",
  },
  {
    icon: Zap,
    title: "Ejecuta el trabajo de verdad",
    desc: "No solo aconseja: envía correos, agenda, actualiza tu CRM, contacta clientes. Acciones reales conectadas a tus herramientas — con tu permiso.",
  },
  {
    icon: TrendingUp,
    title: "Capta clientes y crece",
    desc: "Encuentra negocios a los que vender, redacta el mensaje perfecto y contacta por email. Proactivo: trabaja también cuando tú no estás.",
  },
];

const STEPS = [
  { n: "01", title: "Elige tu sector", desc: "Escoge una plantilla y tienes al instante una plantilla de agentes especializados en tu tipo de negocio." },
  { n: "02", title: "Ajusta tu equipo", desc: "Añade, quita o edita agentes. Tú decides qué hace cada uno y con qué tono habla." },
  { n: "03", title: "Da permisos y a trabajar", desc: "Marca qué puede hacer cada agente solo y qué necesita tu aprobación. Ellos hacen el trabajo, tú mandas." },
];

const AUTONOMY = [
  { lvl: "L1", name: "Aconseja", desc: "Solo sugiere, no toca nada." },
  { lvl: "L2", name: "Prepara", desc: "Deja la acción lista para que la envíes tú." },
  { lvl: "L3", name: "Ejecuta", desc: "Actúa solo en lo que le permitas." },
  { lvl: "L4", name: "Con tu OK", desc: "Te pide permiso antes de lo sensible." },
  { lvl: "L5", name: "Bloqueado", desc: "Nunca, hasta que tú lo actives." },
];

const PLANS = [
  { name: "Starter", price: "29", tag: "Para empezar", feats: ["1 negocio", "Agentes esenciales", "Asistente 24/7", "Soporte por email"], cta: "Empezar gratis", popular: false },
  { name: "Pro", price: "79", tag: "El más elegido", feats: ["Todo lo de Starter", "Agentes ilimitados", "Acciones que ejecutan", "Captación de clientes", "Editar y crear agentes"], cta: "Empezar gratis", popular: true },
  { name: "Business", price: "199", tag: "Para escalar", feats: ["Todo lo de Pro", "Varios usuarios", "Prioridad y auditoría", "Cumplimiento avanzado"], cta: "Empezar gratis", popular: false },
];

const Section = ({ children, className = "" }) => (
  <section className={`max-w-6xl mx-auto px-6 ${className}`}>{children}</section>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-[var(--border)] bg-[var(--bg)]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <BrandMark className="w-8 h-8" />
            <div className="leading-none">
              <div className="font-display font-black text-[15px] tracking-tight">AGENT COMPANY OS</div>
              <div className="label-mono mt-0.5">por BeautyOS</div>
            </div>
          </div>
          <nav className="flex items-center gap-2 md:gap-4">
            <Link to="/precios" className="hidden sm:block text-sm text-[var(--text-dim)] hover:text-white">Precios</Link>
            <Link to="/login" className="text-sm text-[var(--text-dim)] hover:text-white">Entrar</Link>
            <Link to="/register">
              <Button className="rounded-none h-9 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold text-sm">
                Empezar gratis
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <div className="relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <Section className="relative z-10 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 border border-[var(--border)] px-3 py-1 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-[var(--accent)]" />
                <span className="label-mono">Sistema operativo de empleados IA</span>
              </div>
              <h1 className="font-display font-black text-4xl md:text-6xl leading-[0.98] tracking-tight mb-6">
                Monta tu equipo de <span className="text-[var(--accent)]">empleados con IA</span> que trabajan solos.
              </h1>
              <p className="text-lg text-[var(--text-dim)] leading-relaxed mb-8 max-w-xl">
                Elige tu sector y en minutos tienes agentes que atienden a tus clientes, ejecutan tareas de verdad
                y captan negocio — 24/7. Tú das los permisos, ellos hacen el trabajo.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link to="/register">
                  <Button className="rounded-none h-12 px-6 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold text-base">
                    Empezar gratis <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/servicio">
                  <Button variant="outline" className="rounded-none h-12 px-6 border-[var(--border)] hover:border-[var(--accent)] text-base">
                    Que te lo montemos <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-6 text-xs text-[var(--text-dim)]">
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--good)]" /> 14 días gratis</span>
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--good)]" /> En español</span>
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--good)]" /> Tú apruebas cada acción sensible</span>
              </div>
            </div>

            {/* Hero visual: mock command center */}
            <div className="relative">
              <div className="border border-[var(--border)] bg-[var(--surface)] scanlines relative">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--border)]">
                  <div className="w-2.5 h-2.5 rounded-full bg-[var(--danger)]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[var(--warn)]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[var(--good)]" />
                  <span className="label-mono ml-2">tu-negocio · centro de mando</span>
                </div>
                <div className="p-4 space-y-3 font-mono text-[13px]">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 text-[var(--accent)] mt-0.5 shrink-0" />
                    <div><span className="text-[var(--text-dim)]">Cliente:</span> ¿Tenéis mesa para 4 esta noche?</div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Bot className="w-4 h-4 text-[var(--good)] mt-0.5 shrink-0" />
                    <div>¡Sí! Reservado a las 21:00 a nombre de Ana. Te envío la confirmación 👍</div>
                  </div>
                  <div className="border-t border-[var(--border)] pt-3 space-y-1.5 text-[var(--text-dim)]">
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[var(--good)]" /> Reserva creada</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[var(--good)]" /> WhatsApp de confirmación enviado</div>
                    <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[var(--warn)]" /> Recordatorio programado 24h antes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* SECTORES */}
      <Section className="py-12 border-b border-[var(--border)]">
        <p className="text-center label-mono mb-6">Una plantilla para cada negocio · 16 sectores listos</p>
        <div className="flex flex-wrap justify-center gap-2.5">
          {SECTORS.map((s) => (
            <Link key={s.slug} to={`/para/${s.slug}`}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm text-[var(--text-dim)] hover-neon hover:text-white">
              {s.name}
            </Link>
          ))}
        </div>
      </Section>

      {/* VALUES */}
      <Section className="py-20">
        <div className="text-center mb-14">
          <div className="label-mono mb-3">Qué hace por ti</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">
            No es un chatbot. Es un equipo que trabaja.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {VALUES.map((v) => (
            <div key={v.title} className="border border-[var(--border)] bg-[var(--surface)] p-6 hover-neon">
              <div className="w-11 h-11 border border-[var(--accent)] flex items-center justify-center mb-5">
                <v.icon className="w-5 h-5 text-[var(--accent)]" strokeWidth={2} />
              </div>
              <h3 className="font-display font-bold text-xl mb-2 tracking-tight">{v.title}</h3>
              <p className="text-sm text-[var(--text-dim)] leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CÓMO FUNCIONA */}
      <div className="border-y border-[var(--border)] bg-[var(--surface)]/40">
        <Section className="py-20">
          <div className="text-center mb-14">
            <div className="label-mono mb-3">En 3 pasos</div>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Listo en una tarde</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="relative border border-[var(--border)] bg-[var(--bg)] p-6">
                <div className="font-display font-black text-5xl text-[var(--accent)]/30 mb-3">{s.n}</div>
                <h3 className="font-display font-bold text-lg mb-2 tracking-tight">{s.title}</h3>
                <p className="text-sm text-[var(--text-dim)] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* AUTONOMÍA / CONTROL */}
      <Section className="py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-mono mb-3">Tú mandas, siempre</div>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-5">
              Autonomía real, con permisos que decides tú.
            </h2>
            <p className="text-[var(--text-dim)] leading-relaxed mb-6">
              Cada agente tiene un nivel de permiso. Empieza con todo bajo tu control y suéltalo poco a poco,
              a medida que confías. Cada acción queda registrada y tienes un botón de emergencia para pararlo todo.
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--good)]" /> Aprobación humana</span>
              <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-[var(--good)]" /> Auditoría de todo</span>
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[var(--good)]" /> Botón de parada</span>
            </div>
          </div>
          <div className="space-y-2.5">
            {AUTONOMY.map((a) => (
              <div key={a.lvl} className="flex items-center gap-4 border border-[var(--border)] bg-[var(--surface)] px-4 py-3">
                <span className="font-mono font-bold text-[var(--accent)] w-8">{a.lvl}</span>
                <span className="font-semibold text-sm w-24">{a.name}</span>
                <span className="text-sm text-[var(--text-dim)] flex-1">{a.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* NÚMEROS */}
      <div className="border-y border-[var(--border)] bg-[var(--surface)]/40">
        <Section className="py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { big: "16", small: "Sectores listos" },
              { big: "245", small: "Agentes especializados" },
              { big: "24/7", small: "Sin descanso" },
              { big: "0€", small: "Para empezar" },
            ].map((n) => (
              <div key={n.small}>
                <div className="font-display font-black text-4xl md:text-5xl text-[var(--accent)]">{n.big}</div>
                <div className="label-mono mt-2">{n.small}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* PRECIOS */}
      <Section className="py-20" >
        <div className="text-center mb-14">
          <div className="label-mono mb-3">Precios claros</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Empieza gratis. Crece cuando quieras.</h2>
          <p className="text-[var(--text-dim)] mt-3">Usa tu propia clave de IA o el plan con IA incluida. Cancela cuando quieras.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {PLANS.map((p) => (
            <div key={p.name} className={`border bg-[var(--surface)] p-6 flex flex-col ${p.popular ? "border-[var(--accent)]" : "border-[var(--border)]"}`}>
              {p.popular && <div className="label-mono text-[var(--accent)] mb-2">★ {p.tag}</div>}
              {!p.popular && <div className="label-mono mb-2">{p.tag}</div>}
              <div className="font-display font-bold text-2xl tracking-tight">{p.name}</div>
              <div className="mt-3 mb-5">
                <span className="font-display font-black text-4xl">{p.price}€</span>
                <span className="text-[var(--text-dim)] text-sm"> /mes</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {p.feats.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-[var(--good)] mt-0.5 shrink-0" />
                    <span className="text-[var(--text-dim)]">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button className={`w-full rounded-none h-11 font-semibold ${p.popular ? "bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" : "bg-transparent border border-[var(--border)] hover:border-[var(--accent)] text-white"}`}>
                  {p.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA FINAL */}
      <div className="border-t border-[var(--border)] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <Section className="relative z-10 py-20 text-center">
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight mb-5">
            Monta tu equipo de IA hoy.
          </h2>
          <p className="text-[var(--text-dim)] text-lg mb-8 max-w-xl mx-auto">
            Sin instalaciones, sin contratar a nadie. Elige tu sector y empieza gratis en minutos.
          </p>
          <Link to="/register">
            <Button className="rounded-none h-12 px-8 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold text-base">
              Empezar gratis <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </Section>
      </div>

      {/* FOOTER */}
      <footer className="border-t border-[var(--border)] bg-[var(--surface)]">
        <Section className="py-10">
          <div className="flex flex-col md:flex-row justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BrandMark className="w-7 h-7" />
                <span className="font-display font-black text-sm tracking-tight">AGENT COMPANY OS</span>
              </div>
              <p className="text-xs text-[var(--text-dim)] max-w-xs">Empleados con IA para tu negocio. Por BeautyOS · Eduardo Sánchez Velayos.</p>
            </div>
            <div className="flex gap-10 text-sm">
              <div className="space-y-2">
                <div className="label-mono mb-1">Producto</div>
                <Link to="/precios" className="block text-[var(--text-dim)] hover:text-white">Precios</Link>
                <Link to="/servicio" className="block text-[var(--text-dim)] hover:text-white">Servicio gestionado</Link>
                <Link to="/register" className="block text-[var(--text-dim)] hover:text-white">Empezar gratis</Link>
                <Link to="/login" className="block text-[var(--text-dim)] hover:text-white">Entrar</Link>
              </div>
              <div className="space-y-2">
                <div className="label-mono mb-1">Contacto</div>
                <a href="mailto:hola@beautyos.es" className="block text-[var(--text-dim)] hover:text-white">hola@beautyos.es</a>
              </div>
              <div className="space-y-2">
                <div className="label-mono mb-1">Legal</div>
                <Link to="/privacidad" className="block text-[var(--text-dim)] hover:text-white">Privacidad</Link>
                <Link to="/cookies" className="block text-[var(--text-dim)] hover:text-white">Cookies</Link>
                <Link to="/terminos" className="block text-[var(--text-dim)] hover:text-white">Términos</Link>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[var(--border)] text-xs text-[var(--text-dim)]">
            © 2026 BeautyOS · Agent Company OS. Todos los derechos reservados.
          </div>
        </Section>
      </footer>
    </div>
  );
}
